/**
 * PostCreatorV2 — Single-page topic-first post generator
 * (Master Spec v4 + mockup użytkownika `cImage 10 maj 2026, 21_42_17.png`)
 *
 * Layout: PanelLayoutV2 wrapper + 2 kolumny (form left, preview right sticky)
 *
 * 4 sekcje formularza (numerowane):
 *  1. O czym chcesz napisać post? — 9 kafelków 3x3
 *  2. Napisz krótko, co chcesz przekazać — textarea 0/200
 *  3. Dodaj zdjęcia — grid z X i Dodaj więcej (max 10)
 *  4. Jaki jest cel tego posta? — 5 kafelków celów (opcjonalnie)
 *
 * Preview (right):
 *  - FB-style preview z avatarem hodowli, treścią, hashtagami, siatką zdjęć, reactions
 *  - "Wersje posta do wyboru" — 3 karty (emocjonalna/sprzedażowa/profesjonalna) z radio-style
 *  - "Gdzie chcesz opublikować?" — 4 kafelki (Facebook/Grupy/Katalog/Szkic)
 *  - Opublikuj teraz + Zaplanuj na później
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Lightbulb, Send, X, ImagePlus, Loader2, Edit3,
  Heart, MessageCircle, Share2 as ShareIcon, Check,
  PawPrint, Trophy, Megaphone, UserPlus, Calendar as CalIcon, Bone,
  Leaf, Info, Pencil, MessagesSquare, Users, LayoutGrid as Catalog, Save,
  ChevronDown, Smile, Menu,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import StudioShell from '@/components/studio/StudioShell';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { chunkedUpload, validateFile } from '@/utils/chunkedUpload';
import { getImageUrl } from '@/utils/imageUrl';
import { firePawConfetti } from '@/utils/confetti';
import { recordPostPublish } from '@/utils/postStreak';
import { haptics } from '@/utils/haptics';
import PostStreakModal from '@/components/PostStreakModal';

const TOPICS = [
  { id: 'puppy_photos', label: 'Nowe zdjęcia szczeniąt', icon: Users },
  { id: 'free_puppy', label: 'Wolny samiec', icon: Bone },
  { id: 'planned_litter', label: 'Planowany miot', icon: UserPlus },
  { id: 'show_success', label: 'Sukces na wystawie', icon: Trophy },
  { id: 'stud', label: 'Reproduktor', icon: PawPrint },
  { id: 'daily_life', label: 'Codzienność hodowli', icon: Leaf },
  { id: 'contact_invite', label: 'Zaproszenie do kontaktu', icon: UserPlus },
  { id: 'availability_info', label: 'Informacja o dostępności', icon: Info },
  { id: 'other', label: 'Inny temat', icon: Pencil },
];

const GOALS = [
  { id: 'more_messages', label: 'Więcej wiadomości', icon: MessagesSquare },
  { id: 'promote_litter', label: 'Promocja miotu', icon: Bone },
  { id: 'show_dog', label: 'Pokażę psa', icon: PawPrint },
  { id: 'build_trust', label: 'Budowanie zaufania', icon: UserPlus },
  { id: 'boost_reach', label: 'Zwiększenie zasięgu', icon: Megaphone },
];

const DESTINATIONS = [
  { id: 'facebook', label: 'Facebook', sub: 'Profil i grupa', icon: Send },
  { id: 'groups', label: 'Grupy hodowlane', sub: '2 grupy wybrane', icon: Users },
  { id: 'catalog', label: 'Katalog APISAI', sub: 'Twój profil', icon: Catalog },
  { id: 'draft', label: 'Zapisz jako szkic', sub: 'Tylko dla Ciebie', icon: Save },
];

const VERSION_META = {
  emotional: { label: 'Bardziej emocjonalna', sub: 'Pełna ciepła, buduje więź i pokazuje emocje.', color: 'bg-rose-100 text-rose-600' },
  sales: { label: 'Bardziej sprzedażowa', sub: 'Skupiona na korzyściach i dostępności.', color: 'bg-amber-100 text-amber-600' },
  professional: { label: 'Bardziej profesjonalna', sub: 'Rzeczowa, konkretna i wiarygodna.', color: 'bg-blue-100 text-blue-600' },
};

const BRIEF_MAX = 200;

export default function PostCreatorV2() {
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [brief, setBrief] = useState('');
  const [photos, setPhotos] = useState([]);
  const [goal, setGoal] = useState(null);
  const [destinations, setDestinations] = useState(['facebook']);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [kennel, setKennel] = useState(null);
  const [streakInfo, setStreakInfo] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const photoRef = useRef(null);

  // Load primary kennel for context
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/kennels`, { headers: { Authorization: `Bearer ${token}` } });
        if (Array.isArray(res.data) && res.data[0]) setKennel(res.data[0]);
      } catch {}
    })();
  }, []);

  const generateVersions = useCallback(async () => {
    if (!topic) return;
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/posts/topic-create`,
        {
          topic,
          brief_text: brief,
          photos,
          goal,
          context: { kennel_name: kennel?.name, breeds: kennel?.breeds, city: kennel?.city },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const vs = res.data?.versions || [];
      setVersions(vs);
      if (vs[0]?.id && !selectedVersionId) setSelectedVersionId(vs[0].id);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Nie udało się wygenerować';
      toast.error(typeof detail === 'string' ? detail : 'Błąd generowania');
    } finally {
      setGenerating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, brief, goal, kennel, photos]);

  // Auto-generate when topic changes or brief is substantial
  useEffect(() => {
    if (!topic) { setVersions([]); return; }
    const t = setTimeout(() => generateVersions(), brief.length > 0 ? 800 : 100);
    return () => clearTimeout(t);
  }, [topic, brief, goal, generateVersions]);

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    const token = localStorage.getItem('token');
    const newUrls = [];
    for (const file of files) {
      if (photos.length + newUrls.length >= 10) break;
      const v = validateFile(file);
      if (!v.valid) { toast.error(v.error); continue; }
      try {
        const r = await chunkedUpload(file, token);
        newUrls.push(r.urls?.large || r.urls?.medium || r.url);
      } catch {}
    }
    setPhotos((p) => [...p, ...newUrls].slice(0, 10));
    if (newUrls.length) toast.success(`Dodano ${newUrls.length} zdjęć`);
  };

  const toggleDestination = (id) => {
    if (id === 'draft') {
      setDestinations(['draft']);
      return;
    }
    setDestinations((prev) => {
      const without = prev.filter((d) => d !== 'draft');
      return without.includes(id) ? without.filter((d) => d !== id) : [...without, id];
    });
  };

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);

  const handlePublish = async () => {
    if (!topic) { toast.error('Wybierz temat'); return; }
    if (!selectedVersion) { toast.error('Poczekaj na wygenerowanie wersji'); return; }
    if (destinations.length === 0) { toast.error('Wybierz gdzie opublikować'); return; }

    setPublishing(true);
    haptics.heavy();
    try {
      const text = `${selectedVersion.body}\n\n${(selectedVersion.hashtags || []).join(' ')}`;
      // Copy do schowka jako fallback
      try { await navigator.clipboard.writeText(text); } catch {}

      if (destinations.includes('facebook')) {
        const enc = encodeURIComponent(text);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${enc}`,
          '_blank'
        );
      }

      firePawConfetti();
      toast.success(destinations.includes('draft') ? 'Zapisano jako szkic' : 'Post gotowy do publikacji');

      const info = recordPostPublish();
      if (info.isNew && [3, 7, 14, 30].includes(info.streak)) {
        setTimeout(() => setStreakInfo(info), 1200);
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <StudioShell mode="workspace" activeKey="promo" contextLabel="Kreator postów">
    <div className="min-h-screen text-[var(--apisai-text-primary)] flex" data-testid="post-creator-v2">
      {streakInfo && <PostStreakModal streak={streakInfo.streak} onClose={() => setStreakInfo(null)} />}

      <main className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        {/* TOP BAR */}
        <header className="sticky top-0 z-20 bg-[#080612]/90 backdrop-blur-md border-b border-white/5">
          <div className="px-4 sm:px-8 py-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="post-creator-v2-mobile-menu"
                className="lg:hidden w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0"
                aria-label="Menu"
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-3xl font-bold text-white tracking-tight">Utwórz post</h1>
                <p className="hidden sm:block text-zinc-400 text-sm mt-1">Napisz o tym, co chcesz pokazać — pomożemy stworzyć najlepszy post.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button type="button" variant="outline" data-testid="post-creator-v2-ideas-btn" className="hidden md:flex bg-white text-gray-700 border-white/20 hover:bg-gray-100">
                <Lightbulb className="w-4 h-4 mr-1.5" /> Pomysły na posty
              </Button>
              <Button
                type="button"
                onClick={handlePublish}
                disabled={!selectedVersion || publishing}
                data-testid="post-creator-v2-top-publish-btn"
                className="bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-500/30"
              >
                {publishing ? <Loader2 className="w-4 h-4 sm:mr-1.5 animate-spin" /> : <Send className="w-4 h-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">Zaplanuj lub opublikuj</span>
                <ChevronDown className="hidden sm:block w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <div className="flex-1 px-4 sm:px-8 py-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_500px] gap-6">
            {/* LEFT — FORM */}
            <section className="space-y-5">
              {/* Section 1: Topic */}
              <FormSection num={1} title="O czym chcesz napisać post?" subtitle="Wybierz temat lub wpisz własny pomysł.">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {TOPICS.map((t) => {
                    const Icon = t.icon;
                    const isSelected = topic === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => { haptics.select(); setTopic(t.id); }}
                        data-testid={`post-creator-v2-topic-${t.id}`}
                        className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${
                          isSelected
                            ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-200 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-violet-300 hover:shadow-sm'
                        }`}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-violet-600' : 'text-gray-500'}`} />
                        <span className={`text-xs sm:text-sm font-medium leading-tight ${isSelected ? 'text-violet-900' : 'text-gray-800'}`}>
                          {t.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </FormSection>

              {/* Section 2: Brief */}
              <FormSection num={2} title="Napisz krótko, co chcesz przekazać" subtitle="Kilka słów od Ciebie wystarczy.">
                <div className="relative">
                  <Textarea
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="np. Mamy wolnego samca, Figo zdobył kolejny tytuł, Nowe zdjęcia miotu…"
                    rows={4}
                    maxLength={BRIEF_MAX}
                    data-testid="post-creator-v2-brief"
                    className="resize-none bg-white text-gray-900 border-gray-200 focus-visible:ring-violet-400 pr-16"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-2 text-gray-400 text-xs">
                    <span>{brief.length}/{BRIEF_MAX}</span>
                    <Smile className="w-4 h-4" />
                  </div>
                </div>
              </FormSection>

              {/* Section 3: Photos */}
              <FormSection num={3} title="Dodaj zdjęcia" subtitle="Dodaj zdjęcia lub wideo do posta.">
                <input ref={photoRef} type="file" accept="image/*,video/mp4" multiple className="hidden" onChange={(e) => uploadPhotos(Array.from(e.target.files || []))} />
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 group">
                      <img src={getImageUrl(p) || p} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))} className="absolute top-1.5 right-1.5 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center">
                        <X className="w-3.5 h-3.5 text-gray-700" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 10 && (
                    <button
                      type="button"
                      onClick={() => photoRef.current?.click()}
                      data-testid="post-creator-v2-add-photo"
                      className="aspect-square rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/40 hover:bg-violet-50 flex flex-col items-center justify-center text-violet-600"
                    >
                      <ImagePlus className="w-6 h-6" />
                      <span className="text-xs font-medium mt-1">Dodaj więcej</span>
                      <span className="text-[10px] text-gray-500 mt-0.5">JPG / PNG / MP4</span>
                      <span className="text-[10px] text-gray-400">Maks. 10 plików</span>
                    </button>
                  )}
                </div>
              </FormSection>

              {/* Section 4: Goal */}
              <FormSection num={4} title="Jaki jest cel tego posta?" subtitle="Dzięki temu dopasujemy treść i wezwanie do działania." optional>
                <div className="flex flex-wrap gap-2">
                  {GOALS.map((g) => {
                    const Icon = g.icon;
                    const isSelected = goal === g.id;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => { haptics.select(); setGoal(isSelected ? null : g.id); }}
                        data-testid={`post-creator-v2-goal-${g.id}`}
                        className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border min-w-[110px] transition-all ${
                          isSelected
                            ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-200 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-violet-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-violet-100' : 'bg-gray-100'}`}>
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-violet-600' : 'text-gray-500'}`} />
                        </div>
                        <span className={`text-xs font-medium ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>{g.label}</span>
                      </button>
                    );
                  })}
                </div>
              </FormSection>

              {/* Info note */}
              <div className="flex gap-2.5 p-3.5 rounded-xl bg-violet-50/60 border border-violet-100">
                <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-700 leading-relaxed">
                  APISAI automatycznie użyje danych Twojej hodowli (nazwa, rasa, kontakt), aby post był kompletny i gotowy do publikacji.
                </p>
              </div>
            </section>

            {/* RIGHT — PREVIEW */}
            <section className="space-y-5 min-w-0">
              <div className="xl:sticky xl:top-32 space-y-4">
                {/* Preview Card */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Podgląd posta (Facebook)</h3>
                    <button type="button" className="text-violet-600 hover:text-violet-700 text-sm font-medium flex items-center gap-1" data-testid="post-creator-v2-edit-look">
                      <Edit3 className="w-3.5 h-3.5" /> Edytuj wygląd
                    </button>
                  </div>
                  <FBPostMock data={selectedVersion} kennel={kennel} photos={photos} generating={generating} />
                </div>

                {/* Versions */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Wersje posta do wyboru</h3>
                    <button
                      type="button"
                      onClick={generateVersions}
                      disabled={!topic || generating}
                      data-testid="post-creator-v2-regenerate-btn"
                      className="text-violet-600 hover:text-violet-700 text-sm font-medium disabled:opacity-50"
                    >
                      {generating ? 'Generuję…' : 'Wygeneruj nowe'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Wybierz wersję, która najbardziej Ci odpowiada lub dostosuj ją po swojemu.</p>
                  <div className="space-y-2">
                    {versions.length === 0 && !generating && (
                      <p className="text-sm text-gray-400 italic text-center py-6">
                        {topic ? 'Generuję 3 wersje…' : 'Wybierz temat aby zobaczyć propozycje.'}
                      </p>
                    )}
                    {generating && versions.length === 0 && (
                      <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
                    )}
                    {versions.map((v) => {
                      const meta = VERSION_META[v.id] || { label: v.label, sub: '', color: 'bg-gray-100 text-gray-600' };
                      const isSelected = v.id === selectedVersionId;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setSelectedVersionId(v.id)}
                          data-testid={`post-creator-v2-version-${v.id}`}
                          className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                            isSelected ? 'border-violet-400 bg-violet-50/40 shadow-sm' : 'border-gray-200 bg-white hover:border-violet-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mb-1.5">{meta.sub}</p>
                          {v.error ? (
                            <p className="text-xs text-red-600">{v.error}</p>
                          ) : (
                            <p className="text-xs text-gray-700 line-clamp-3 whitespace-pre-line">{v.body}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Destinations */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Gdzie chcesz opublikować?</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {DESTINATIONS.map((d) => {
                      const Icon = d.icon;
                      const isSelected = destinations.includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => toggleDestination(d.id)}
                          data-testid={`post-creator-v2-dest-${d.id}`}
                          className={`flex items-center gap-2 p-3 rounded-xl border transition-all text-left ${
                            isSelected ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-200' : 'border-gray-200 bg-white hover:border-violet-300'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-violet-100' : 'bg-gray-100'}`}>
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-violet-600' : 'text-gray-500'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-semibold leading-tight ${isSelected ? 'text-violet-900' : 'text-gray-800'}`}>{d.label}</p>
                            <p className="text-[10px] text-gray-500 truncate">{d.sub}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <button type="button" className="text-violet-600 hover:text-violet-700 text-xs font-medium flex items-center gap-1" data-testid="post-creator-v2-schedule-later">
                      <CalIcon className="w-3.5 h-3.5" /> Zaplanuj na później
                    </button>
                    <Button
                      type="button"
                      onClick={handlePublish}
                      disabled={!selectedVersion || publishing || destinations.length === 0}
                      data-testid="post-creator-v2-publish-btn"
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      {publishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                      Opublikuj teraz
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
    </StudioShell>
  );
}

function FormSection({ num, title, subtitle, optional, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 text-gray-900">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
          {num}
        </div>
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
            {title} {optional && <span className="text-gray-400 font-normal text-sm">(opcjonalnie)</span>}
          </h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function FBPostMock({ data, kennel, photos, generating }) {
  const name = kennel?.name || 'Twoja hodowla';
  const sub = kennel?.breeds?.[0] ? `Hodowla ${kennel.breeds[0]}` : 'Hodowla psów';

  return (
    <div className="bg-white">
      <div className="flex items-center gap-3 p-3 border-b border-gray-100">
        {kennel?.cover_image ? (
          <img src={getImageUrl(kennel.cover_image) || kennel.cover_image} alt={name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 truncate">{name}</p>
          <p className="text-xs text-gray-500 truncate">{sub} · teraz</p>
        </div>
      </div>
      <div className="p-3">
        {generating && !data ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Generuję wersję…
          </div>
        ) : data ? (
          <>
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{data.body}</p>
            {(data.hashtags || []).length > 0 && (
              <p className="text-sm text-violet-600 mt-2">{data.hashtags.join(' ')}</p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400 italic">Tu pojawi się gotowa wersja Twojego posta…</p>
        )}
      </div>
      {photos.length > 0 && (
        <div className={`grid ${photos.length === 1 ? 'grid-cols-1' : photos.length === 2 ? 'grid-cols-2' : 'grid-cols-4'} gap-0.5`}>
          {photos.slice(0, 4).map((p, i) => (
            <div key={i} className="aspect-square bg-gray-100 relative">
              <img src={getImageUrl(p) || p} alt="" className="w-full h-full object-cover" />
              {i === 3 && photos.length > 4 && (
                <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  +{photos.length - 3}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
        <span className="flex items-center gap-0.5">
          <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white text-[8px]">👍</span>
          <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-[8px]">❤</span>
          <span className="ml-1">128</span>
        </span>
        <span>15 komentarzy · 8 udostępnień</span>
      </div>
      <div className="flex justify-around p-1 border-t border-gray-100 text-xs text-gray-600">
        <button className="px-3 py-2 flex items-center gap-1 hover:bg-gray-50 rounded"><Heart className="w-4 h-4" /> Lubię to!</button>
        <button className="px-3 py-2 flex items-center gap-1 hover:bg-gray-50 rounded"><MessageCircle className="w-4 h-4" /> Komentarz</button>
        <button className="px-3 py-2 flex items-center gap-1 hover:bg-gray-50 rounded"><ShareIcon className="w-4 h-4" /> Udostępnij</button>
      </div>
    </div>
  );
}
